import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useMst } from '../../core/stores';
import { Typography, Tabs, Tab } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { OrdersPanel } from './OrdersPanel';
import { OrderStatus } from '../../models';
import queryString from 'query-string';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { startOfDay, endOfDay, getUnixTime, subMonths } from 'date-fns';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '60%',
    margin: 'auto',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  pageLabel: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
  },
  orders: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& > *': {
      margin: theme.spacing(2),
      marginBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(1),
    },
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  datePickers: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  datePicker: {
    width: '35%',
    marginLeft: theme.spacing(1),
  },
}));

export interface DOProps extends RouteComponentProps<{}> {}

const tabOrder = [OrderStatus.SUBMITTED, OrderStatus.INCOMPLETE, OrderStatus.FULFILLED];

// Fulfilled orders default to the last three months when no explicit start date is chosen.
const FULFILLED_DEFAULT_MONTHS = 3;

export const ManageOrders: React.FC<DOProps> = observer(({ location }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const urlParams = queryString.parse(location.search);
  const { digitalOrdersStore } = useMst();
  const [existingOrders, setExistingOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const tabValue = useMemo(() => tabOrder.indexOf(urlParams?.status as OrderStatus), [urlParams]);

  useEffect(() => {
    loadAllOrders();
  }, [tabValue, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const getExistingOrders = async () => {
    const status = tabOrder[tabValue] ?? OrderStatus.SUBMITTED;
    const effectiveStart =
      startDate ??
      (status === OrderStatus.FULFILLED
        ? subMonths(startOfDay(new Date()), FULFILLED_DEFAULT_MONTHS)
        : null);
    const params = Object.assign(
      { status: [status] },
      effectiveStart && { startDate: getUnixTime(effectiveStart) },
      endDate && { endDate: getUnixTime(endDate) },
    );

    return await digitalOrdersStore.loadDigitalOrders(params);
  };

  const loadAllOrders = () => {
    getExistingOrders().then(response => {
      setOrdersLoading(false);
      setExistingOrders(response);
    });
  };

  const handleTabChange = newValue => {
    const newPath = queryString.stringifyUrl({
      url: location.pathname,
      query: { status: tabOrder[newValue] },
    });
    history.push(newPath);
  };

  const handleDatePickerChange = (picker, date) => {
    picker === 'start'
      ? setStartDate(date ? startOfDay(date) : null)
      : setEndDate(date ? endOfDay(date) : null);
  };

  return (
    <div className={classes.container}>
      <Typography className={classes.pageLabel}>Manage Existing Orders</Typography>
      <div className={classes.orders}>
        <div className={classes.topBar}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => handleTabChange(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="standard"
            style={{ marginBottom: '0', height: '48px' }}
          >
            <Tab label="Submitted" />
            <Tab label="Pending" />
            <Tab label="Fulfilled" />
          </Tabs>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <div className={classes.datePickers}>
              <KeyboardDatePicker
                label={'Orders After'}
                className={classes.datePicker}
                onChange={val => handleDatePickerChange('start', val)}
                maxDate={new Date()}
                value={startDate}
                clearable
                disableFuture
                autoOk
                allowKeyboardControl={false}
              />
              <KeyboardDatePicker
                label={'Orders Before'}
                className={classes.datePicker}
                onChange={val => handleDatePickerChange('end', val)}
                maxDate={new Date()}
                value={endDate}
                clearable
                disableFuture
                autoOk
                allowKeyboardControl={false}
              />
            </div>
          </MuiPickersUtilsProvider>
        </div>
        <TabPanel value={tabValue} index={0}>
          <OrdersPanel
            orders={existingOrders}
            ordersLoading={ordersLoading}
            loadOrders={loadAllOrders}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <OrdersPanel
            orders={existingOrders}
            ordersLoading={ordersLoading}
            loadOrders={loadAllOrders}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <OrdersPanel
            orders={existingOrders}
            ordersLoading={ordersLoading}
            loadOrders={loadAllOrders}
            readOnly
          />
        </TabPanel>
      </div>
    </div>
  );
});

type TabPanelProps = {
  children: ReactNode;
  index: any;
  value: any;
};
const TabPanel: React.FC<TabPanelProps> = props => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {children}
    </div>
  );
};
